// Số của KHUNG HIỆU SUẤT cho trang Đánh giá (quyết định 0058).
//
// Trang này trước đây tự nghĩ ra một bộ điểm riêng từ đơn + khiếu nại + khen
// thưởng ("tiêu chí proxy", chính nó ghi thế trong chân bảng). Hai bộ điểm song
// song cho cùng một câu hỏi "ai làm tốt" là nguồn cãi nhau: hai màn hình, hai
// con số, không ai biết tin cái nào. Từ 01/09/2026 trang đọc THẲNG sổ điểm đơn
// của khung — một nguồn duy nhất.
//
// Riêng cột "hài lòng" của bản cũ còn sai nghĩa: nó đọc `orders.satisfaction`
// (điểm 0–10 trưởng hậu kỳ chấm ẢNH) rồi trình bày như thang 5 sao của KHÁCH.
// Bỏ hẳn — sao khách nằm ở `happyPts` do máy chủ tính, trần 20.

import type { OrderScore } from "@/features/performance/api";

/**
 * Nhãn đơn, theo đúng enum của hợp đồng API.
 *
 * `unmeasured` KHÔNG phải một mức điểm mà là "chưa có gì để chấm": đơn không
 * phiếu lỗi, không mốc hành trình, không sao khách. Trước 03/09/2026 đơn như
 * thế ra 100 điểm xanh — khối Chất lượng coi "0 lỗi" là trọn trần rồi ăn luôn
 * trần của hai khối kia — nên một hệ chưa ghi lỗi bao giờ hiện ra bảng toàn
 * màu xanh, trông y hệt bằng chứng làm tốt.
 */
export type ScoreColor = "green" | "yellow" | "red" | "unmeasured";

export type ScoreSummary = {
  /** Số đơn ĐÃ ĐO ĐƯỢC — mẫu số của mọi trung bình và mọi tỷ lệ bên dưới. */
  scored: number;
  /**
   * Số đơn CHƯA đo được. Đứng ngoài mọi phép tính khác.
   *
   * Đây là con số nói lên ĐỘ PHỦ của việc chấm: `unmeasured` lớn hơn `scored`
   * nghĩa là bảng đang mô tả một nhúm nhỏ, đọc mấy ô % kia phải nhớ điều đó.
   */
  unmeasured: number;
  avgTotal: number;
  avgQuality: number;
  avgSpeed: number;
  avgHappy: number;
  green: number;
  yellow: number;
  red: number;
  greenPct: number;
  yellowPct: number;
  redPct: number;
};

const EMPTY: ScoreSummary = {
  scored: 0,
  unmeasured: 0,
  avgTotal: 0,
  avgQuality: 0,
  avgSpeed: 0,
  avgHappy: 0,
  green: 0,
  yellow: 0,
  red: 0,
  greenPct: 0,
  yellowPct: 0,
  redPct: 0,
};

/**
 * Khối Đúng hẹn / Khách chấm có đo được không. Dòng điểm cũ (trước 15/10/2026)
 * chưa có cờ: coi khối có điểm > 0 là đã đo — cùng luật với máy chủ.
 */
export function blockMeasured(flag: boolean | undefined, pts: number | undefined): boolean {
  return flag ?? (pts ?? 0) > 0;
}

function round1(n: number): number {
  return Math.round(n * 10) / 10;
}

/**
 * Gộp một nhúm nhãn đơn thành một dòng số.
 *
 * Tỷ lệ tính trên SỐ ĐƠN CÓ ĐIỂM, không trên tổng đơn của tháng: đơn chưa chấm
 * xong thì chưa có màu, và đem nó vào mẫu số sẽ kéo "% xanh" xuống mỗi đầu
 * tháng rồi tự bò lên — một đường cong không nói gì về chất lượng.
 */
export function summarizeOrderScores(rows: OrderScore[]): ScoreSummary {
  if (rows.length === 0) return { ...EMPTY };
  let total = 0;
  let quality = 0;
  let speed = 0;
  let happy = 0;
  let speedN = 0;
  let happyN = 0;
  let green = 0;
  let yellow = 0;
  let red = 0;
  let unmeasured = 0;
  for (const r of rows) {
    // Đơn chưa đo được chỉ được ĐẾM, không được cộng vào tổng nào: điểm 0 của
    // nó là chỗ trống chứ không phải kết quả, mà cộng chỗ trống vào trung bình
    // là kéo điểm của cả tháng xuống vì một chuyện chưa ai làm.
    if (r.color === "unmeasured") {
      unmeasured += 1;
      continue;
    }
    total += r.totalPts ?? 0;
    quality += r.qualityPts ?? 0;
    // Khối chưa đo được (N4, rà soát 15/10/2026): điểm 0 của nó là chỗ trống —
    // trần đã chia cho khối khác — nên chỉ chia trung bình cho đơn ĐÃ đo khối đó.
    if (blockMeasured(r.speedMeasured, r.speedPts)) {
      speed += r.speedPts ?? 0;
      speedN += 1;
    }
    if (blockMeasured(r.happyMeasured, r.happyPts)) {
      happy += r.happyPts ?? 0;
      happyN += 1;
    }
    if (r.color === "green") green += 1;
    else if (r.color === "yellow") yellow += 1;
    else if (r.color === "red") red += 1;
  }
  const n = rows.length - unmeasured;
  // Cả tháng chưa đo được đơn nào: giữ nguyên số đơn chưa đo, mọi ô còn lại về
  // 0. Chia cho 0 ở đây sẽ vẽ NaN lên màn quản lý, trông y hệt hệ thống hỏng.
  if (n === 0) return { ...EMPTY, unmeasured };
  return {
    scored: n,
    unmeasured,
    avgTotal: round1(total / n),
    avgQuality: round1(quality / n),
    avgSpeed: speedN ? round1(speed / speedN) : 0,
    avgHappy: happyN ? round1(happy / happyN) : 0,
    green,
    yellow,
    red,
    greenPct: round1((green * 100) / n),
    yellowPct: round1((yellow * 100) / n),
    redPct: round1((red * 100) / n),
  };
}

export type BranchScoreRow = ScoreSummary & { branch: string };

/**
 * Cùng bộ số đó, tách theo CƠ SỞ.
 *
 * Tách theo cơ sở chứ không theo phòng ban: một đơn đi qua nhiều phòng nên
 * không quy về một phòng được, còn điểm THEO NGƯỜI và THEO PHÒNG đã có màn
 * riêng trong khung (Đội / Giám sát) — dựng lại ở đây là hai bảng cùng nội
 * dung, rồi sẽ lệch nhau khi một bên được sửa.
 *
 * Xếp % đỏ giảm dần: người mở trang này đi tìm chỗ đang cháy, không đi tìm
 * bảng chữ cái.
 */
export function groupOrderScoresByBranch(rows: OrderScore[]): BranchScoreRow[] {
  const buckets = new Map<string, OrderScore[]>();
  for (const r of rows) {
    const key = r.branch ?? "";
    const list = buckets.get(key);
    if (list) list.push(r);
    else buckets.set(key, [r]);
  }
  const out: BranchScoreRow[] = [];
  for (const [branch, list] of buckets) {
    out.push({ branch, ...summarizeOrderScores(list) });
  }
  out.sort((a, b) => b.redPct - a.redPct || b.scored - a.scored || a.branch.localeCompare(b.branch));
  return out;
}

export const COLOR_LABEL: Record<ScoreColor, string> = {
  green: "🟢 Xanh",
  yellow: "🟡 Vàng",
  red: "🔴 Đỏ",
  unmeasured: "⚪ Chưa đo được",
};

/**
 * Lớp màu chữ cho ô tổng điểm. Đỏ mới nhấn — vàng là nhắc, không phải báo động.
 *
 * "Chưa đo được" làm mờ đi: nó là chỗ trống, và một chỗ trống được tô đậm sẽ
 * kéo mắt người đọc khỏi những đơn thật sự đang có vấn đề.
 */
export function colorClass(color: string | undefined): string {
  if (color === "red") return "text-destructive font-semibold";
  if (color === "yellow") return "text-warning-text";
  if (color === "unmeasured") return "text-muted-foreground";
  return "";
}

/**
 * Bộ lọc màu của thẻ "Theo đơn". Ngoài bốn nhãn còn `scored` = mọi đơn ĐÃ ĐO
 * (xanh + vàng + đỏ) — đích của ô "Đơn có điểm" trên Tổng quan, để con số bấm
 * vào ra đúng chừng ấy dòng.
 */
export type ScoreFilter = ScoreColor | "scored" | "";

export function matchesScoreFilter(color: string, filter: ScoreFilter): boolean {
  if (filter === "") return true;
  if (filter === "scored") return color !== "unmeasured";
  return color === filter;
}

export type ScoreSortKey = "totalPts" | "qualityPts" | "speedPts" | "happyPts";

/**
 * Xếp danh sách đơn theo một cột điểm. Đơn chưa đo LUÔN nằm cuối, theo chiều
 * nào cũng vậy: điểm 0 của nó là chỗ trống, xếp chung là nó chiếm trọn đầu
 * danh sách "thấp nhất" và đẩy những đơn thật sự kém xuống dưới tầm mắt.
 */
export function sortOrderScores(
  rows: OrderScore[],
  key: ScoreSortKey,
  dir: "asc" | "desc",
): OrderScore[] {
  const sign = dir === "asc" ? 1 : -1;
  return [...rows].sort((a, b) => {
    const au = a.color === "unmeasured";
    const bu = b.color === "unmeasured";
    if (au !== bu) return au ? 1 : -1;
    return sign * ((a[key] ?? 0) - (b[key] ?? 0)) || a.orderId.localeCompare(b.orderId);
  });
}

/** Trần GỐC của từng khối, trước khi máy chia lại trần của khối chưa đo được. */
export const BASE_CAP: Record<Exclude<ScoreSortKey, "totalPts">, number> = {
  qualityPts: 40,
  speedPts: 40,
  happyPts: 20,
};
